import React, { useMemo, useState } from 'react';
import {
  Modal,
  Box,
  Typography,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Tooltip,
  Alert,
} from '@mui/material';
import {
  Close as CloseIcon,
  TrendingUp as TrendingUpIcon,
  Air as AirIcon,
  Whatshot as WhatshotIcon,
  PlayArrow as PlayArrowIcon,
  Build as BuildIcon,
  LocalFireDepartment as FireIcon,
  CalendarToday as CalendarIcon,
  Insights as InsightsIcon,
} from '@mui/icons-material';
import { useParams } from 'react-router-dom';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import SeasonalTrendsView from './SeasonalTrendsView';
import WindRoseView from './WindRoseView';
import FireDangerIndexView from './FireDangerIndexView';
import RunSimulationsView from './RunSimulationsView';
import CustomBuildView from './CustomBuildView';
import RunDateView from './RunDateView';
import FDICalendarDay from './FDICalendarDay';
import ClimateProjectionsView from './ClimateProjectionsView';
import SeasonalOutlookView from './SeasonalOutlookView';
import { useWeatherHistoryDateRange, useDailyFDI } from '../../api/weatherHistory';
import { useProject } from '../../api/project';
import { useDate } from '../../contexts/DateContext';
import { useTimezone } from '../../contexts/TimezoneContext';
import { useStrategy } from '../../api/strategy';
import { SimulationParams } from '../../contexts/SimulationContext';
import dayjs from 'dayjs';

interface WeatherProbeModalProps {
  open: boolean;
  onClose: () => void;
  onGenerate?: (params?: SimulationParams) => void;
}

type ViewType = 'seasonal' | 'wind' | 'fire-danger' | 'climate-projections' | 'seasonal-outlook' | 'run-date' | 'simulations' | 'custom';

const WeatherProbeModal: React.FC<WeatherProbeModalProps> = ({ open, onClose, onGenerate }) => {
  const { projectId } = useParams();
  const [activeView, setActiveView] = useState<ViewType>('seasonal');
  const [customPresetValues, setCustomPresetValues] = useState<any>(null);
  const [customPresetName, setCustomPresetName] = useState<string | undefined>(undefined);
  const [runDatePresetValues, setRunDatePresetValues] = useState<any>(null);
  
  const { project } = useProject();
  const { data: dateRange, isLoading: isLoadingDateRange } = useWeatherHistoryDateRange(projectId || '');
  const { selectedDate, setSelectedDate } = useDate();
  const { selectedTimezone } = useTimezone();
  const { isLoading: isStrategyLoading } = useStrategy();

  // Local weather date — browsing weather without changing the global project date
  const [weatherDate, setWeatherDate] = useState<dayjs.Dayjs>(selectedDate);

  // Fetch all daily FDI values (historical + forecast) once for calendar coloring
  const { data: dailyFDI } = useDailyFDI(projectId || '');

  const fdiByDate = useMemo(() => {
    if (!Array.isArray(dailyFDI)) return {};
    const map: Record<string, number> = {};
    for (const entry of dailyFDI) {
      map[entry.date] = entry.fdi;
    }
    return map;
  }, [dailyFDI]);

  // Re-sync state when the modal opens
  const prevOpenRef = React.useRef(false);
  React.useEffect(() => {
    if (open && !prevOpenRef.current) {
      setWeatherDate(selectedDate);
      setCustomPresetValues(null);
      setCustomPresetName(undefined);
      setRunDatePresetValues(null);
    }
    prevOpenRef.current = open;
  }, [open, selectedDate]);
  
  const FORECAST_MAX_FUTURE_DAYS = 14;
  const earliestArchiveDate = dayjs('1940-01-01');
  const latestForecastDate = dayjs().add(FORECAST_MAX_FUTURE_DAYS, 'day');


  const handlePresetSelect = (preset: any) => {
    if (preset.timestamp) {
      // Historical extreme day - switch to Run Date tab with that date
      const presetDate = dayjs(preset.timestamp);
      setWeatherDate(presetDate);
      setRunDatePresetValues({
        droughtFactor: preset.drought_factor,
        hour: presetDate.hour(),
      });
      setActiveView('run-date');
    } else if (preset.preset_type === 'date' && preset.params?.date) {
      // User-created date preset
      const presetDate = dayjs(preset.params.date);
      setWeatherDate(presetDate);
      setRunDatePresetValues({
        droughtFactor: preset.params.drought_factor,
        droughtIndex: preset.params.drought_index,
        curing: preset.params.curing,
        hour: preset.params.hour ?? 10,
        duration: preset.params.duration,
        spottingMode: preset.params.spotting_mode,
        weatherOverrides: preset.params.weather_overrides,
      });
      setActiveView('run-date');
    } else if (preset.preset_type === 'custom' && preset.params) {
      // User-created custom preset
      setCustomPresetValues({
        temperature: preset.params.temperature,
        humidity: preset.params.humidity,
        windSpeed: preset.params.wind_speed,
        windDirection: preset.params.wind_direction_deg,
        droughtFactor: preset.params.drought_factor,
        droughtIndex: preset.params.drought_index,
        curing: preset.params.curing,
        spottingMode: preset.params.spotting_mode,
        weatherOverrides: preset.params.weather_overrides,
      });
      setCustomPresetName(preset.name);
      setActiveView('custom');
    } else {
      // Standard scenario or climate projection - switch to Run Custom tab with preset values
      setCustomPresetValues({
        temperature: preset.temperature,
        humidity: preset.humidity,
        windSpeed: preset.windSpeed || preset.wind_speed,
        windDirection: preset.windDirection || 0,
        droughtFactor: preset.droughtFactor || preset.drought_factor,
      });
      setCustomPresetName(preset.name || undefined);
      setActiveView('custom');
    }
  };

  const navigationItems = [
    { id: 'seasonal' as ViewType, label: 'Seasonal Trends', icon: <TrendingUpIcon /> },
    { id: 'wind' as ViewType, label: 'Wind Rose', icon: <AirIcon /> },
    { id: 'fire-danger' as ViewType, label: 'Fire Danger Index', icon: <WhatshotIcon /> },
    { id: 'climate-projections' as ViewType, label: 'Climate Projections', icon: <FireIcon /> },
  ];

  const actionItems = [
    { id: 'seasonal-outlook' as ViewType, label: 'Seasonal Outlook', icon: <InsightsIcon /> },
    { id: 'run-date' as ViewType, label: 'Run Date', icon: <CalendarIcon /> },
    { id: 'custom' as ViewType, label: 'Run Custom', icon: <BuildIcon /> },
    { id: 'simulations' as ViewType, label: 'Presets', icon: <PlayArrowIcon /> },
  ];

  // A new project opens before its 46-year weather record has been fetched;
  // say so instead of showing empty charts and an empty preset list.
  const weatherLayer = project?.data?.layer_status?.weather?.state;
  const weatherNotice =
    weatherLayer === 'pending' || weatherLayer === 'loading' ? (
      <Alert severity="info" icon={<CircularProgress size={16} />} sx={{ mb: 1.5 }}>
        Weather history is still loading for this project. These charts and the presets fill in
        as soon as it arrives, usually within a few minutes.
      </Alert>
    ) : weatherLayer === 'failed' ? (
      <Alert severity="warning" sx={{ mb: 1.5 }}>
        Weather history couldn't be loaded for this project
        {project?.data?.layer_status?.weather?.error ? `: ${project.data.layer_status.weather.error}` : ''}.
        Custom weather still works; the charts and presets need the history.
      </Alert>
    ) : null;

  const renderView = () => {
    if (!projectId) return null;
    const view = renderActiveView();
    return weatherNotice ? (
      <>
        {weatherNotice}
        {view}
      </>
    ) : view;
  };

  const renderActiveView = () => {
    if (!projectId) return null;

    switch (activeView) {
      case 'seasonal':
        return <SeasonalTrendsView projectId={projectId} weatherDate={weatherDate} />;
      case 'wind':
        return <WindRoseView projectId={projectId} />;
      case 'fire-danger':
        return <FireDangerIndexView projectId={projectId} />;
      case 'climate-projections':
        return <ClimateProjectionsView projectId={projectId} />;
      case 'seasonal-outlook':
        return <SeasonalOutlookView projectId={projectId} onGenerate={onGenerate} onClose={onClose} weatherDate={weatherDate} projectDate={selectedDate} />;
      case 'run-date':
        return <RunDateView projectId={projectId} onGenerate={onGenerate} onClose={onClose} presetValues={runDatePresetValues} weatherDate={weatherDate} projectDate={selectedDate} />;
      case 'simulations':
        return <RunSimulationsView projectId={projectId} onPresetSelect={handlePresetSelect} />;
      case 'custom':
        return <CustomBuildView projectId={projectId} onGenerate={onGenerate} onClose={onClose} presetValues={customPresetValues} presetName={customPresetName} weatherDate={weatherDate} projectDate={selectedDate} />;
      default:
        return null;
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          height: '85%',
          bgcolor: '#1a1d2e',
          boxShadow: 24,
          display: 'flex',
          borderRadius: 2,
          overflow: 'hidden',
        }}
      >

        {/* Sidebar */}
        <Box
          sx={{
            width: 260,
            bgcolor: '#0f1117',
            borderRight: '1px solid rgba(255, 255, 255, 0.1)',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          {/* Header */}
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              <FireIcon sx={{ color: '#ff6b35' }} />
              <Typography variant="h6" sx={{ color: '#fff', fontWeight: 600 }}>
                Local Weather
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
              Project: {project?.data?.name || 'Loading...'}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.5)', display: 'block' }}>
              Historical Data: {isLoadingDateRange ? (
                <CircularProgress size={12} sx={{ ml: 1 }} />
              ) : dateRange?.minDate && dateRange?.maxDate 
                ? `${dateRange.minDate.getFullYear()}-${dateRange.maxDate.getFullYear()}`
                : 'No data'}
            </Typography>
          </Box>

          {/* Date Pickers */}
          <Box sx={{ p: 2, borderBottom: '1px solid rgba(255, 255, 255, 0.1)', display: 'flex', flexDirection: 'column', gap: 2 }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <Tooltip
                title="The landscape date — fuel loads, vegetation, and mitigations are calculated as of this date"
                placement="right"
                arrow
              >
                <Box>
                  <DatePicker
                    label="Project Date"
                    value={selectedDate}
                    onChange={() => {}}
                    onAccept={(newDate) => {
                      if (newDate && newDate.isValid()) {
                        setSelectedDate(newDate.tz(selectedTimezone));
                      }
                    }}
                    disabled={isStrategyLoading}
                    format="YYYY/MM/DD"
                    slotProps={{
                      popper: {
                        sx: {
                          '& .MuiPaper-root': {
                            bgcolor: '#1e1e2f',
                            color: '#fff',
                          },
                          '& .MuiPickersCalendarHeader-label': {
                            color: '#fff',
                          },
                          '& .MuiDayCalendar-weekDayLabel': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                          '& .MuiPickersDay-root': {
                            color: '#fff',
                          },
                          '& .MuiPickersArrowSwitcher-button': {
                            color: '#fff',
                          },
                          '& .MuiIconButton-root': {
                            color: '#fff',
                          },
                          '& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton': {
                            color: '#fff',
                          },
                        },
                      },
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.23)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.4)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#ff6b35',
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(255, 255, 255, 0.3)',
                              '& fieldset': {
                                borderColor: 'rgba(255, 255, 255, 0.12)',
                              },
                            },
                          },
                          '& .MuiInputBase-input.Mui-disabled': {
                            WebkitTextFillColor: 'rgba(255, 255, 255, 0.3)',
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&.Mui-focused': {
                              color: '#ff6b35',
                            },
                            '&.Mui-disabled': {
                              color: 'rgba(255, 255, 255, 0.3)',
                            },
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                          '& .Mui-disabled .MuiSvgIcon-root': {
                            color: 'rgba(255, 255, 255, 0.2)',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Tooltip>
              <Tooltip
                title="The weather source date — historical or forecast weather conditions are pulled from this date for the simulation"
                placement="right"
                arrow
              >
                <Box>
                  <DatePicker
                    label="Weather Date"
                    value={weatherDate}
                    onChange={() => {}}
                    onAccept={(newDate) => {
                      if (newDate && newDate.isValid()) {
                        setWeatherDate(newDate.tz(selectedTimezone));
                        setRunDatePresetValues(null);
                      }
                    }}
                    format="YYYY/MM/DD"
                    minDate={earliestArchiveDate}
                    maxDate={latestForecastDate}
                    slots={{ day: FDICalendarDay }}
                    slotProps={{
                      day: { fdiByDate } as any,
                      popper: {
                        sx: {
                          '& .MuiPaper-root': {
                            bgcolor: '#1e1e2f',
                            color: '#fff',
                          },
                          '& .MuiPickersCalendarHeader-label': {
                            color: '#fff',
                          },
                          '& .MuiDayCalendar-weekDayLabel': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                          '& .MuiPickersDay-root': {
                            color: '#fff',
                          },
                          '& .MuiPickersArrowSwitcher-button': {
                            color: '#fff',
                          },
                          '& .MuiIconButton-root': {
                            color: '#fff',
                          },
                          '& .MuiPickersYear-yearButton, & .MuiPickersMonth-monthButton': {
                            color: '#fff',
                          },
                        },
                      },
                      textField: {
                        size: 'small',
                        fullWidth: true,
                        sx: {
                          '& .MuiOutlinedInput-root': {
                            color: '#fff',
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.23)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.4)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#ff6b35',
                            },
                          },
                          '& .MuiInputLabel-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                            '&.Mui-focused': {
                              color: '#ff6b35',
                            },
                          },
                          '& .MuiSvgIcon-root': {
                            color: 'rgba(255, 255, 255, 0.7)',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Tooltip>
            </LocalizationProvider>
          </Box>

          {/* Analysis Views */}
          <Box sx={{ p: 1 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.4)',
                px: 2,
                py: 1,
                display: 'block',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: 1,
              }}
            >
              Analysis View
            </Typography>
            <List sx={{ p: 0 }}>
              {navigationItems.map((item) => (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    selected={activeView === item.id}
                    onClick={() => setActiveView(item.id)}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      '&.Mui-selected': {
                        bgcolor: '#ff6b35',
                        '&:hover': {
                          bgcolor: '#ff7f50',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 107, 53, 0.1)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: activeView === item.id ? '#fff' : 'rgba(255, 255, 255, 0.6)', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        color: activeView === item.id ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>

          {/* Action Items */}
          <Box sx={{ p: 1, mt: 2 }}>
            <Typography
              variant="caption"
              sx={{
                color: 'rgba(255, 255, 255, 0.4)',
                px: 2,
                py: 1,
                display: 'block',
                textTransform: 'uppercase',
                fontSize: '0.7rem',
                letterSpacing: 1,
              }}
            >
              Action
            </Typography>
            <List sx={{ p: 0 }}>
              {actionItems.map((item) => (
                <ListItem key={item.id} disablePadding>
                  <ListItemButton
                    selected={activeView === item.id}
                    onClick={() => setActiveView(item.id)}
                    sx={{
                      borderRadius: 1,
                      mx: 1,
                      '&.Mui-selected': {
                        bgcolor: '#ff6b35',
                        '&:hover': {
                          bgcolor: '#ff7f50',
                        },
                      },
                      '&:hover': {
                        bgcolor: 'rgba(255, 107, 53, 0.1)',
                      },
                    }}
                  >
                    <ListItemIcon sx={{ color: activeView === item.id ? '#fff' : 'rgba(255, 255, 255, 0.6)', minWidth: 40 }}>
                      {item.icon}
                    </ListItemIcon>
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{
                        fontSize: '0.9rem',
                        color: activeView === item.id ? '#fff' : 'rgba(255, 255, 255, 0.7)',
                        fontWeight: activeView === item.id ? 600 : 400,
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </List>
          </Box>
        </Box>

        {/* Main Content */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Close Button */}
          <Box sx={{ position: 'absolute', top: 16, right: 16, zIndex: 1 }}>
            <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {/* View Content */}
          <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
            {renderView()}
          </Box>
        </Box>
      </Box>
    </Modal>
  );
};

export default WeatherProbeModal;
